import { motion } from "framer-motion";
import { cn } from "@/lib/utils";

/**
 * WizardStepper — minimal, low-cognitive-load progress header.
 *
 * Progress is anchored to the three macro-phases (Conexão · Atendimento ·
 * IA & Lançamento), which are STABLE, instead of a raw question count that
 * drifts as conditional questions appear/disappear.
 *
 * `steps` must be the CANONICAL question list (all questions, in order), not
 * the currently-visible subset. Within a phase, progress = the current
 * question's fixed position in the canonical order. Using the visible subset
 * would make the denominator grow the moment an answer reveals later questions
 * in the same phase (e.g. picking an area reveals "área principal"/"triagem"),
 * which read as the bar going BACKWARD. With the canonical order, answering
 * never moves the bar; only navigating to another question does — skipped
 * questions are jumped over, so it advances and never regresses.
 */
const PHASES = ["Conexão", "Atendimento", "IA & Lançamento"];

function phaseColor(active, done) {
  if (active) return "text-[rgb(var(--cyan))]";
  if (done) return "text-foreground/70";
  return "text-muted-foreground/50";
}

export default function WizardStepper({ steps, currentId }) {
  const current = steps.find((s) => s.id === currentId) || steps[0];
  const currentPhase = current?.phase;
  const currentPhaseIdx = Math.max(0, PHASES.indexOf(currentPhase));

  const phaseSteps = steps.filter((s) => s.phase === currentPhase);
  const idxInPhase = Math.max(
    0,
    phaseSteps.findIndex((s) => s.id === current?.id),
  );
  const withinPhase = phaseSteps.length > 0 ? (idxInPhase + 1) / phaseSteps.length : 1;
  const progress = ((currentPhaseIdx + withinPhase) / PHASES.length) * 100;

  return (
    <div className="mb-8">
      {/* Phases */}
      <ol className="flex items-center gap-2 text-[11px] font-medium uppercase tracking-wide">
        {PHASES.map((phase, i) => {
          const active = phase === currentPhase;
          const done = currentPhaseIdx > i;
          return (
            <li key={phase} className="flex items-center gap-2">
              <span className={cn("transition-colors", phaseColor(active, done))}>
                {phase}
              </span>
              {i < PHASES.length - 1 && (
                <span className="h-px w-5 bg-border" aria-hidden="true" />
              )}
            </li>
          );
        })}
      </ol>

      {/* Progress bar */}
      <div
        className="mt-3 h-1 overflow-hidden rounded-full bg-border/70"
        role="progressbar"
        aria-valuenow={Math.round(progress)}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-label={`${currentPhase}: ${current?.label}`}
      >
        <motion.div
          className="h-full rounded-full bg-[rgb(var(--cyan))]"
          initial={false}
          animate={{ width: `${progress}%` }}
          transition={{ duration: 0.45, ease: [0.16, 1, 0.3, 1] }}
        />
      </div>

      {/* Caption — phase (of 3, stable) + current question label. No drifting
          total, so the wizard never reads as if it's getting longer. */}
      <p className="mt-2 text-xs text-muted-foreground">
        Etapa {currentPhaseIdx + 1} de {PHASES.length}
        <span className="mx-1.5 text-border">·</span>
        <span className="font-medium text-foreground">{current?.label}</span>
      </p>
    </div>
  );
}
